import { supabase } from "@/integrations/supabase/client";
import { Tables } from "@/integrations/supabase/types";

export type Organization = Tables<"organizations">;

export interface OrganizationHierarchyNode extends Organization {
  children?: OrganizationHierarchyNode[];
  childCount?: number;
  depth?: number;
  path?: string[];
  metrics?: {
    contactCount?: number;
    opportunityCount?: number;
    opportunityValue?: number;
    activityCount?: number;
    childOrganizationCount?: number;
  };
}

export interface OrganizationHierarchyData {
  rootNode: OrganizationHierarchyNode;
  ancestors: OrganizationHierarchyNode[];
  siblings: OrganizationHierarchyNode[];
  descendants: OrganizationHierarchyNode[];
  flattenedHierarchy: OrganizationHierarchyNode[];
}

const DEFAULT_NODE_DEPTH = 3;

export const fetchCompleteHierarchyForOrganization = async (
  organizationId: string,
  options: {
    maxDepth?: number;
    includeMetrics?: boolean;
    includeRoot?: boolean;
    includeSiblings?: boolean;
  } = {}
): Promise<OrganizationHierarchyData> => {
  const {
    maxDepth = DEFAULT_NODE_DEPTH,
    includeMetrics = true,
    includeRoot = true,
    includeSiblings = true,
  } = options;

  try {
    // Fetch the target organization to get its hierarchy info
    const { data: targetOrg, error: targetError } = await supabase
      .from("organizations")
      .select("*, parent:parent_id(*)")
      .eq("id", organizationId)
      .single();

    if (targetError) throw targetError;
    if (!targetOrg) throw new Error("Organization not found");

    // Find all ancestors recursively
    const ancestors: OrganizationHierarchyNode[] = [];
    if (targetOrg.parent_id) {
      const { data: ancestorData, error: ancestorError } = await supabase
        .from("organizations")
        .select("*")
        .filter("hierarchy_path", "cs", `{${organizationId}}`)
        .not("id", "eq", organizationId)
        .order("hierarchy_level", { ascending: true });

      if (ancestorError) throw ancestorError;
      ancestors.push(...(ancestorData || []));
    }

    // Find siblings if needed
    let siblings: OrganizationHierarchyNode[] = [];
    if (includeSiblings && targetOrg.parent_id) {
      const { data: siblingData, error: siblingError } = await supabase
        .from("organizations")
        .select("*")
        .eq("parent_id", targetOrg.parent_id)
        .not("id", "eq", organizationId)
        .order("name");

      if (siblingError) throw siblingError;
      siblings = siblingData || [];
    }

    // Fetch all descendants up to maxDepth
    const { data: descendantsData, error: descendantsError } = await supabase
      .from("organizations")
      .select("*, parent:parent_id(*)")
      .filter("hierarchy_path", "cs", `{${organizationId}}`)
      .not("id", "eq", organizationId)
      .order("name");

    if (descendantsError) throw descendantsError;
    const descendants = descendantsData || [];

    // Build a hierarchical tree from the flat data
    const buildHierarchyTree = (
      nodes: OrganizationHierarchyNode[],
      parentId: string | null,
      currentDepth: number = 0
    ): OrganizationHierarchyNode[] => {
      // Stop if we've reached max depth
      if (currentDepth > maxDepth) return [];

      return nodes
        .filter((node) => node.parent_id === parentId)
        .map((node) => {
          const children = buildHierarchyTree(nodes, node.id, currentDepth + 1);
          return {
            ...node,
            children,
            childCount: children.length,
            depth: currentDepth,
          };
        });
    };

    // Start building the tree with the organization itself as the root
    const rootNode: OrganizationHierarchyNode = {
      ...targetOrg,
      depth: 0,
      children: buildHierarchyTree(descendants, organizationId, 1),
      childCount: descendants.filter((d) => d.parent_id === organizationId).length,
    };

    // Get metrics for organizations if requested
    if (includeMetrics) {
      // Get all organization IDs in the hierarchy
      const allOrgIds = [
        organizationId,
        ...descendants.map((d) => d.id),
      ];
      
      // Get contact counts
      const { data: contactsData, error: contactsError } = await supabase
        .from("contacts")
        .select("organization_id")
        .in("organization_id", allOrgIds);
      
      if (contactsError) throw contactsError;
      
      // Process the data to get counts per organization
      const contactCountData = contactsData ? contactsData.reduce((acc, contact) => {
        if (!acc[contact.organization_id]) {
          acc[contact.organization_id] = { organization_id: contact.organization_id, count: 0 };
        }
        acc[contact.organization_id].count++;
        return acc;
      }, {} as Record<string, { organization_id: string, count: number }>): {};
      
      // Convert to array format for consistency with the original code
      const contactCounts = Object.values(contactCountData);
      
      // Get opportunity counts and values
      const { data: opportunityData, error: opportunityError } = await supabase
        .from("opportunities")
        .select("organization_id, value")
        .in("organization_id", allOrgIds);
      
      if (opportunityError) throw opportunityError;
      
      // Get activity counts
      const { data: activitiesRawData, error: activitiesError } = await supabase
        .from("activities")
        .select("organization_id")
        .in("organization_id", allOrgIds);
        
      if (activitiesError) throw activitiesError;
      
      // Process the data to get counts per organization  
      const activityCountData = activitiesRawData ? activitiesRawData.reduce((acc, activity) => {
        if (!acc[activity.organization_id]) {
          acc[activity.organization_id] = { organization_id: activity.organization_id, count: 0 };
        }
        acc[activity.organization_id].count++;
        return acc;
      }, {} as Record<string, { organization_id: string, count: number }>): {};
      
      // Convert to array format for consistency with the original code
      const activityData = Object.values(activityCountData);
      
      // Prepare metrics map
      const metricsByOrgId: Record<string, OrganizationHierarchyNode["metrics"]> = {};
      
      // Calculate contact counts
      for (const row of (contactCounts || [])) {
        const orgId = row.organization_id;
        if (!metricsByOrgId[orgId]) metricsByOrgId[orgId] = {};
        metricsByOrgId[orgId]!.contactCount = row.count;
      }
      
      // Calculate opportunity metrics
      const opportunitiesByOrg: Record<string, { count: number; value: number }> = {};
      for (const opp of (opportunityData || [])) {
        const orgId = opp.organization_id;
        if (!opportunitiesByOrg[orgId]) {
          opportunitiesByOrg[orgId] = { count: 0, value: 0 };
        }
        opportunitiesByOrg[orgId].count++;
        opportunitiesByOrg[orgId].value += (opp.value || 0);
      }
      
      for (const [orgId, metrics] of Object.entries(opportunitiesByOrg)) {
        if (!metricsByOrgId[orgId]) metricsByOrgId[orgId] = {};
        metricsByOrgId[orgId]!.opportunityCount = metrics.count;
        metricsByOrgId[orgId]!.opportunityValue = metrics.value;
      }
      
      // Calculate activity counts
      for (const row of (activityData || [])) {
        const orgId = row.organization_id;
        if (!metricsByOrgId[orgId]) metricsByOrgId[orgId] = {};
        metricsByOrgId[orgId]!.activityCount = parseInt(row.count);
      }
      
      // Calculate child organization counts
      for (const org of descendants) {
        const parentId = org.parent_id;
        if (parentId) {
          if (!metricsByOrgId[parentId]) metricsByOrgId[parentId] = {};
          metricsByOrgId[parentId]!.childOrganizationCount = 
            (metricsByOrgId[parentId]!.childOrganizationCount || 0) + 1;
        }
      }
      
      // Helper to apply metrics to a node and its children
      const applyMetricsToNode = (node: OrganizationHierarchyNode): OrganizationHierarchyNode => {
        const updatedNode = {
          ...node,
          metrics: metricsByOrgId[node.id] || {},
        };
        
        if (node.children && node.children.length > 0) {
          updatedNode.children = node.children.map(applyMetricsToNode);
        }
        
        return updatedNode;
      };
      
      // Apply metrics to the root node and all descendants
      rootNode.metrics = metricsByOrgId[rootNode.id] || {};
      if (rootNode.children) {
        rootNode.children = rootNode.children.map(applyMetricsToNode);
      }
    }

    // Create a flattened view of the entire hierarchy for easier rendering
    const flattenHierarchy = (
      node: OrganizationHierarchyNode,
      result: OrganizationHierarchyNode[] = []
    ): OrganizationHierarchyNode[] => {
      result.push(node);
      if (node.children) {
        for (const child of node.children) {
          flattenHierarchy(child, result);
        }
      }
      return result;
    };

    const flattenedHierarchy = flattenHierarchy(rootNode);

    return {
      rootNode,
      ancestors,
      siblings,
      descendants,
      flattenedHierarchy,
    };
  } catch (error) {
    throw error;
  }
};

export const fetchHierarchyMetrics = async (organizationId: string) => {
  try {
    // Get all organizations in the hierarchy
    const { data: hierarchyOrgs, error: hierarchyError } = await supabase
      .from("organizations")
      .select("id, hierarchy_level")
      .or(`hierarchy_path.cs.{${organizationId}},id.eq.${organizationId}`);
    
    if (hierarchyError) throw hierarchyError;
    
    // Get count of organizations in hierarchy
    const organizationCount = hierarchyOrgs?.length || 0;
    const orgIds = hierarchyOrgs?.map(org => org.id) || [];
    
    // Calculate max depth
    const maxDepth = hierarchyOrgs && hierarchyOrgs.length > 0
      ? Math.max(...hierarchyOrgs.map(org => org.hierarchy_level || 0))
      : 0;
    
    // Fetch contacts for these organizations
    const { data: contacts, error: contactsError } = await supabase
      .from("contacts")
      .select("id, organization_id")
      .in("organization_id", orgIds);
      
    if (contactsError) throw contactsError;
    
    // Fetch opportunities for these organizations
    const { data: opportunities, error: opportunitiesError } = await supabase
      .from("opportunities")
      .select("id, value, organization_id")
      .in("organization_id", orgIds);
      
    if (opportunitiesError) throw opportunitiesError;
    
    // Fetch activities for these organizations
    const { data: activities, error: activitiesError } = await supabase
      .from("activities")
      .select("id, organization_id")
      .in("organization_id", orgIds);
      
    if (activitiesError) throw activitiesError;
    
    // Calculate metrics
    const totalContacts = contacts?.length || 0;
    const totalOpportunities = opportunities?.length || 0;
    const totalOpportunityValue = opportunities?.reduce((sum, opp) => sum + (opp.value || 0), 0) || 0;
    const totalActivities = activities?.length || 0;
    
    return {
      organizationCount,
      metrics: {
        total_contacts: totalContacts,
        total_opportunities: totalOpportunities,
        total_opportunity_value: totalOpportunityValue,
        total_activities: totalActivities,
        max_depth: maxDepth
      }
    };
  } catch (error) {
    throw error;
  }
};

export const fetchHierarchyPathToOrganization = async (organizationId: string) => {
  try {
    const { data, error } = await supabase
      .from("organizations")
      .select("id, name, parent_id, hierarchy_path, hierarchy_level")
      .eq("id", organizationId)
      .single();

    if (error) throw error;
    if (!data) throw new Error("Organization not found");

    const path = data.hierarchy_path || [];
    
    if (path.length === 0) {
      return [data];
    }

    // Get all organizations in the path
    const { data: pathOrgs, error: pathError } = await supabase
      .from("organizations")
      .select("id, name, parent_id, hierarchy_level")
      .in("id", path)
      .order("hierarchy_level", { ascending: true });

    if (pathError) throw pathError;

    // Add the target organization to the end of the path
    return [...(pathOrgs || []), data];
  } catch (error) {
    throw error;
  }
};